import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import CheckIcon from 'icons/Check.icon';
import { FILE_SECURITY } from 'router/subrouters/DataRoom/models/DataRoom';
import useMatchMedia from 'hooks/useMatchMedia';
import useCallbackRef from 'hooks/useCallbackRef';
import { ALIGNMENT, Alignment } from 'models/Global';
import FileSecurityLevelCard from '../FileSecurityLevelCard';
import FileSecurityLevelRow from '../FileSecurityLevelRow';
import WatermarkChecklistFields from '../WatermarkChecklistFields';
import { updateCheckboxValue } from '../../../../checkboxUtils';
import {
  isAdvancedSelected,
  isBasicSelected,
  isMaximumSelected,
} from 'router/subrouters/DataRoom/utils';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import PreviewWatermark from '../PreviewWatermark';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { MaxSecurityCardClickedEventProps } from 'models/MixpanelEvents';
import ExpirationChecklistItem from '../ExpirationChecklistItem';
import MaximumSecurityComingSoonModal from '../MaximumSecurityComingSoonModal';
import useSelectLevel from './useSelectLevel';

import './FileSecurityLevels.styles.scss';

type FileSecurityLevelsProps = {
  className?: string;
  layout?: Alignment;
  maximumSecurityComingSoon?: boolean;
  isDisabled?: boolean;
};

const FileSecurityLevels: React.FC<FileSecurityLevelsProps> = (props) => {
  const isPhablet = useMatchMedia('isPhablet');

  const {
    className,
    layout = isPhablet ? ALIGNMENT.HORIZONTAL : ALIGNMENT.VERTICAL,
    maximumSecurityComingSoon = true,
    isDisabled = false,
  } = props;

  const [comingSoonModal, comingSoonModalRef] = useCallbackRef<ModalRef>();

  const {
    dataRoom: { _id: dataRoomId, name: dataRoomName },
  } = useContext(DataRoomContext);

  const classes = classNames(
    'file-security-levels',
    `file-security-levels--${layout}`,
    className,
  );

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { values } = useFormState();
  const { change } = useForm();

  const {
    fileSecurity,
    blurProtection,
    watermarkDiagonal,
    watermarkHeader,
    watermarkFooter,
    watermarkFullName,
    watermarkEmail,
    watermarkIpAddress,
  } = values || {};

  const watermarkPreviewParams = useMemo(
    () => ({
      watermarkDiagonal: !!watermarkDiagonal?.checked,
      watermarkHeader: !!watermarkHeader?.checked,
      watermarkFooter: !!watermarkFooter?.checked,
      watermarkFullName: !!watermarkFullName?.checked,
      watermarkEmail: !!watermarkEmail?.checked,
      watermarkIpAddress: !!watermarkIpAddress?.checked,
    }),
    [
      watermarkDiagonal?.checked,
      watermarkEmail?.checked,
      watermarkFooter?.checked,
      watermarkFullName?.checked,
      watermarkHeader?.checked,
      watermarkIpAddress?.checked,
    ],
  );

  const LevelComponent =
    layout === ALIGNMENT.HORIZONTAL
      ? FileSecurityLevelRow
      : FileSecurityLevelCard;

  const basicSelected = isBasicSelected(fileSecurity);
  const advancedSelected = isAdvancedSelected(fileSecurity);
  const maximumSelected = isMaximumSelected(fileSecurity);

  const selectLevel = useSelectLevel();

  const handleMaxSecurityCardClick = useCallback(() => {
    comingSoonModal?.open();

    mixpanelService.maxSecurityCardClicked(
      getMixpanelProps({
        dataRoomId,
        dataRoomName,
      }) as MaxSecurityCardClickedEventProps,
    );
  }, [comingSoonModal, dataRoomId, dataRoomName, getMixpanelProps]);

  return (
    <>
      <div className={classes}>
        <Field name="fileSecurity" component="input" hidden />

        <LevelComponent
          title="basic"
          description="bestForInformation"
          isSelected={basicSelected}
          onSelect={() => selectLevel(FILE_SECURITY.BASIC)}
          list={[
            { label: 'originalFilesAvailableForDownload' },
            { label: 'noDownloadRestrictions' },
            { label: 'noWatermarkRestrictions' },
          ]}
        />

        <LevelComponent
          title="advanced"
          description="additionalProtectionWithEasyAccess"
          isSelected={advancedSelected}
          disabled={isDisabled}
          onSelect={() => selectLevel(FILE_SECURITY.ADVANCED)}
          list={[
            {
              label: 'supportedFilesConvertedToPdf',
              popupContent: t('supportedFilesConvertedToPdfTooltip'),
            },
          ]}
        >
          <div className="file-security-levels__details">
            <ExpirationChecklistItem isSelected={advancedSelected} />

            <WatermarkChecklistFields disabled={!advancedSelected} />

            <PreviewWatermark
              disabled={!advancedSelected}
              params={watermarkPreviewParams}
            />
          </div>
        </LevelComponent>

        <LevelComponent
          title="maximum"
          description="maximumProtectionForHighlyConfidential"
          isSelected={maximumSelected}
          isNotifyAction={maximumSecurityComingSoon}
          disabled={isDisabled && !maximumSecurityComingSoon}
          onSelect={
            maximumSecurityComingSoon
              ? handleMaxSecurityCardClick
              : () => selectLevel(FILE_SECURITY.MAXIMUM)
          }
          list={[
            {
              label: 'supportedFilesProtectedUsingSecureViewer',
              popupContent: t(
                'supportedFilesProtectedUsingSecureViewerTooltip',
              ),
            },
            { label: 'desktopAppRequiredToOpenDownloadedFiles' },
          ]}
        >
          <div className="file-security-levels__details">
            <ExpirationChecklistItem isSelected={maximumSelected} />

            <div className="checklist-item">
              <CheckIcon />
              <span>{t('embeddedWatermarkIncluded')}</span>
            </div>

            <WatermarkChecklistFields disabled={!maximumSelected} />

            <PreviewWatermark
              disabled={!maximumSelected}
              params={watermarkPreviewParams}
            />

            <Field
              component={CheckboxField}
              label={
                <>
                  {t('blurProtection')}
                  <ResolvePopup
                    onHover
                    popupContent={t('blurProtectionTooltip')}
                  >
                    <InformationcircleIcon />
                  </ResolvePopup>
                </>
              }
              name="blurProtection.checked"
              type="checkbox"
              disabled={!maximumSelected}
              className={classNames({ mixed: blurProtection?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'blurProtection',
                  checked,
                  state: blurProtection?.state,
                  isMixed: blurProtection?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
            <Field name="blurProtection" component="input" hidden />
          </div>
        </LevelComponent>
      </div>

      {maximumSecurityComingSoon && (
        <MaximumSecurityComingSoonModal ref={comingSoonModalRef} />
      )}
    </>
  );
};

export default FileSecurityLevels;
